import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TranslationKey } from '@/types/i18n.types';
import { EButtonHierarchy } from '@/components/common/Button/Button';
import { SLICE_NAMES } from '../constants';

export const PopupActionType = {
  RELOAD: 'reload',
  NAVIGATE: 'navigate',
  CLOSE: 'close',
} as const;

export const POPUP_ACTION_ID = {
  COMPLETE_ONBOARDING: 'completeOnboarding',
  CLOSE_CHECKOUT_MODAL: 'closeCheckoutModal',
  EULA_AGREED: 'eulaAgreed',
} as const;

export const POPUP_CONTENT_ID = {
  TERMS_UPDATED: 'termsUpdated',
} as const;

export type TPopupActionId =
  (typeof POPUP_ACTION_ID)[keyof typeof POPUP_ACTION_ID];

export type TPopupContentId =
  (typeof POPUP_CONTENT_ID)[keyof typeof POPUP_CONTENT_ID];

type PopupAction =
  | { type: typeof PopupActionType.RELOAD }
  | { type: typeof PopupActionType.NAVIGATE; url: string }
  | { type: typeof PopupActionType.CLOSE };

interface IPopupMessageShell {
  buttonAction?: PopupAction;
  actionId?: TPopupActionId;
  showCloseButton?: boolean;
  buttonHierarchy?: EButtonHierarchy;
}

export interface IPopupMessageDefaultConfig extends IPopupMessageShell {
  contentId?: undefined;
  iconSrc?: string;
  titleKey: TranslationKey;
  subtitleKey: TranslationKey;
  buttonTextKey: TranslationKey;
}

export interface IPopupMessageCustomConfig extends IPopupMessageShell {
  contentId: TPopupContentId;
}

export type IPopupMessageConfig =
  | IPopupMessageDefaultConfig
  | IPopupMessageCustomConfig;

interface PopupMessageState {
  queue: IPopupMessageConfig[];
}

const initialState: PopupMessageState = {
  queue: [],
};

const isSamePopup = (
  queued: IPopupMessageConfig,
  incoming: IPopupMessageConfig
): boolean => {
  const queuedTitle = 'titleKey' in queued ? queued.titleKey : undefined;
  const incomingTitle = 'titleKey' in incoming ? incoming.titleKey : undefined;
  const queuedSubtitle =
    'subtitleKey' in queued ? queued.subtitleKey : undefined;
  const incomingSubtitle =
    'subtitleKey' in incoming ? incoming.subtitleKey : undefined;
  return (
    queued.contentId === incoming.contentId &&
    queued.actionId === incoming.actionId &&
    queuedTitle === incomingTitle &&
    queuedSubtitle === incomingSubtitle &&
    queued.buttonAction?.type === incoming.buttonAction?.type
  );
};

const popupMessageSlice = createSlice({
  name: SLICE_NAMES.POPUP_MESSAGE,
  initialState,
  reducers: {
    showPopupMessage: (state, action: PayloadAction<IPopupMessageConfig>) => {
      const alreadyQueued = state.queue.some(config =>
        isSamePopup(config, action.payload)
      );
      if (!alreadyQueued) {
        state.queue.push(action.payload);
      }
    },
    closePopupMessage: state => {
      state.queue.shift();
    },
  },
});

export const { showPopupMessage, closePopupMessage } =
  popupMessageSlice.actions;
export const popupMessageReducer = popupMessageSlice.reducer;
